<script lang="ts">
    import { onDestroy } from 'svelte'

    import type { DateTime } from 'luxon'

    import { gettext as _, ngettext } from 'hydria/services/gettext'
    import { simpleFetch } from 'hydria/services/session'
    import { sanchor, spath } from 'hydria/svelte_helpers'
    import { toLuxon } from 'hydria/utils/time'

    import Date from 'hydria/components/Date.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const UPDATE_TIMEOUT_MS = 8 * 60 * 1000

    interface ADomain {
        idna: string
        domain: string
        tld: string
        expiration: DateTime | null
        unactivated_years: number
    }
    let domainsMissingYears: ADomain[] = $state([])
    let totalDomainsMissingYears: number = $state(0)
    let loading = $state(true)
    let error = $state(false)
    let updating = $state(false)

    let eventTimer: ReturnType<typeof setTimeout> | undefined = undefined

    let fakeExpirationCount = $state(0)

    async function load() {
        if (eventTimer) {
            try {
                clearTimeout(eventTimer)
            } catch {}
            eventTimer = undefined
        }

        if (!loading) updating = true
        let r: any

        try {
            r = await simpleFetch('GET', '/json/domain/1/ready-for-activation')
        } catch (e) {
            console.error('Failed to load domain ready for activation listing:', e)
        }

        domainsMissingYears = []
        totalDomainsMissingYears = 0

        if (r) {
            domainsMissingYears = r.data.items.map((item: ADomain) => {
                item.expiration = item.expiration ? toLuxon(item.expiration) : null
                return item
            })
            totalDomainsMissingYears = r.data.unactivated_count

            fakeExpirationCount = r.data.fake_expiration_count
        } else {
            error = true
        }
        loading = false
        updating = false

        eventTimer = setTimeout(() => load(), UPDATE_TIMEOUT_MS)
    }
    load()

    onDestroy(() => {
        if (eventTimer) {
            try {
                clearTimeout(eventTimer)
            } catch {}
            eventTimer = undefined
        }
    })
</script>

<div class="card">
    <div class="card-title" class:text-danger={domainsMissingYears.length} class:text-secondary={!domainsMissingYears.length}>
        <span>{$_('Domaines avec années non-activées / expiration temporaire')}</span>
        {#if updating}
            <span><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>
        {/if}
    </div>
    {#if loading}
        <div class="card-body d-flex gap-3">
            <div style="flex: 1 0 0">
                <LoadingPattern pattern={['x']} />
            </div>
            <div>{$_('Loading…')}</div>
            <div style="flex: 1 0 0">
                <LoadingPattern pattern={['x']} />
            </div>
        </div>
    {:else if error}
        <div class="card-body"><div class="text-danger">L’information n’a pas pu être chargée.</div></div>
    {:else}
        {#if !totalDomainsMissingYears}
            <div class="card-body">
                <p><a use:sanchor href={spath('/domains?has_missing_years=')}>{$_('Aucun domaine en attente d’activation.')}</a></p>
            </div>
        {:else}
            <div class="card-body">
                {#if !domainsMissingYears.length}
                    <p>{$_('Aucun domaine n’est prêt pour l’activation d’années manquantes.')}</p>
                    <p><a use:sanchor href={spath('/domains?has_missing_years=')}>{$ngettext('Il y a {n} domaine en attente d’activation.', 'Il y a {n} domaines en attente d’activation.', { n: totalDomainsMissingYears })}</a></p>
                {:else}
                    <p><a use:sanchor href={spath('/domains?has_missing_years=')}>{$ngettext('{n} domaine est prêt pour l’activation.', '{n} domaines est prêt pour l’activation.', { n: domainsMissingYears.length })}</a></p>
                {/if}
            </div>
            {#if domainsMissingYears.length}
                <table class="table mb-0">
                    <thead>
                        <tr>
                            <th>Domaine</th>
                            <th>Expiration actuelle</th>
                            <th>Années à activer</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each domainsMissingYears as dom}
                            <tr>
                                <td><a use:sanchor href={spath(`/domain/${dom.idna}`)}>{dom.domain}.{dom.tld}</a></td>
                                <td><Date value={dom.expiration} format="h-table-s" /></td>
                                <td>{dom.unactivated_years}</td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            {/if}
        {/if}
        <div class="card-body">
            <p>
                <a use:sanchor href={spath('/domains?has_fake_expiration=')}>
                    {#if !fakeExpirationCount}
                        {$_('Aucun domaine avec une expiration temporaire.')}
                    {:else}
                        {$ngettext('Il y a {n} domaine avec une expiration temporaire.', 'Il y a {n} domaines avec une expiration temporaire.', { n: fakeExpirationCount })}
                    {/if}
                </a>
            </p>
        </div>
    {/if}
</div>
